import React, { useEffect, useMemo, useRef, useCallback } from "react";
import { Container } from "@pixi/react";
import { useDimensions } from "../../Contexts/DimensionsContext";
import { useReelRefs } from "../../Contexts/ReelRefsContext";
import { useSlot, getCounterScale } from "../../Contexts/SlotRegistryContext";
import { FEATURES_CONFIG } from "../../featuresConfig";
import { useSelector } from "react-redux";
import {
  selectIntroPopupActive,
  selectMenuActive,
  selectRoyalCollectInitialReels,
} from "../../State/selectors";
import { isBonusSymbol } from "../../SymbolData";
import { Settings } from "../../settings";
import RoyalCollectReelBack from "./RoyalCollectReelBack";
import RoyalCollectReelFront from "./RoyalCollectReelFront";
import RoyalCollectReelSet from "./RoyalCollectReelSet";
import RoyalCollectOverlayReelSet from "./RoyalCollectOverlayReelSet";
import RoyalCollectCollectionLayer from "./RoyalCollectCollectionLayer";
import { GAME_CONTAINER_OFFSET_CONFIG } from "../Main/GameContainer/GameContainer";
import { PixiSettings } from "../../pixiSettings";
import Jackpots from "../../Components/Animations/Jackpots/Jackpots";
import RoyalCollectRespinCounter from "./RoyalCollectRespinCounter";
import LogoLandscape from "../../Components/UserInterface/shared/Logos/LogoLandscape";
import LogoPortrait from "../../Components/UserInterface/shared/Logos/LogoPortrait";

/**
 * RoyalCollectGameContainer
 *
 * Self-contained game container for the Royal Collection feature.
 * Attaches to the `feature_reel` spine slot in BackgroundAnimation,
 * so it moves independently from the base game reelset during
 * feature_transition animations.
 *
 * When initialReels data arrives, triggers idle spine animations on all
 * coin positions and hides corresponding static textures.
 */
const RoyalCollectGameContainer = () => {
  const {
    symbolWidth,
    symbolHeight,
    symbolScale,
    dimensions,
    dividerDimensions,
    reelSpacing,
    device,
    orientation,
    isMobileSmall,
  } = useDimensions();

  const {
    royalCollectReelRefs,
    royalCollectOverlayReelRefs,
    royalCollectCollectionLayerRef,
  } = useReelRefs();

  const introPopupActive = useSelector(selectIntroPopupActive);
  const menuActive = useSelector(selectMenuActive);
  const initialReels = useSelector(selectRoyalCollectInitialReels);

  const featureContainerRef = useRef(null);
  const featureReelContainerRef = useRef(null);
  const idleAnimationsTriggeredRef = useRef(false);

  // Inject feature_reel_container into background spine's feature_reel slot via SlotRegistry
  const featureReelSlot = useSlot("background.feature_reel");
  useEffect(() => {
    if (!FEATURES_CONFIG.BACKGROUND.REELSET_BONE_INJECTION) return;
    if (!featureReelSlot || !featureReelContainerRef.current) return;
    const node = featureReelContainerRef.current;
    featureReelSlot.addChild(node);
    return () => {
      if (node.parent === featureReelSlot) {
        featureReelSlot.removeChild(node);
      }
    };
  }, [featureReelSlot]);

  // Counter-scale to undo the spine's inherited scale (bone injection mode only)
  const spineCounterScale = useMemo(
    () => getCounterScale(symbolScale),
    [symbolScale],
  );

  // Apply counter-scale on resize (bone injection mode only)
  useEffect(() => {
    if (!FEATURES_CONFIG.BACKGROUND.REELSET_BONE_INJECTION) return;
    if (featureReelContainerRef.current) {
      featureReelContainerRef.current.scale.set(spineCounterScale);
    }
  }, [spineCounterScale]);

  /**
   * Trigger initial spine animations on all symbol positions.
   * - Bonus symbols (coins, jackpots, royals): show idle animation (looping),
   *   hide static texture underneath (spine overlay takes over).
   * - Blank symbols: skip entirely — they use static textures only, no spine.
   */
  const triggerInitialAnimations = useCallback(() => {
    if (!initialReels || initialReels.length === 0) return;

    for (let reelIndex = 0; reelIndex < initialReels.length; reelIndex++) {
      const reel = initialReels[reelIndex];
      if (!Array.isArray(reel)) continue;

      const overlayReelRef = royalCollectOverlayReelRefs.current?.[reelIndex];
      const reelRef = royalCollectReelRefs.current?.[reelIndex];

      for (
        let symbolIndex = 0;
        symbolIndex < Math.min(reel.length, Settings.SYMBOLS_VISIBLE);
        symbolIndex++
      ) {
        const symbolId = reel[symbolIndex];
        if (symbolId === undefined || symbolId === null) continue;

        // Only bonus symbols get overlay spine animations.
        // Blanks use static textures only — no spine overlay.
        if (!isBonusSymbol(symbolId)) continue;

        const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
        if (!overlaySymbolRef) continue;

        // Coins/jackpots/royals: show idle animation (looping)
        if (overlaySymbolRef.showIdleAnimation) {
          overlaySymbolRef.showIdleAnimation(symbolId);
        }

        // Hide static texture underneath (spine overlay takes over)
        const symbolRef = reelRef?.getSymbolRef?.(symbolIndex);
        if (symbolRef?.current?.hideSymbolTexture) {
          requestAnimationFrame(() => {
            symbolRef.current.hideSymbolTexture();
          });
        }
      }
    }
  }, [initialReels, royalCollectOverlayReelRefs, royalCollectReelRefs]);

  /**
   * When initialReels data arrives (trigger round), start idle animations
   * on all coin positions. This runs once when the feature data first arrives
   * so coins are animated before the transition reveals the reelset.
   */
  useEffect(() => {
    if (
      !initialReels ||
      initialReels.length === 0 ||
      idleAnimationsTriggeredRef.current
    ) {
      return;
    }

    // Small delay to ensure overlay controllers are initialized
    const timeoutId = setTimeout(() => {
      triggerInitialAnimations();
      idleAnimationsTriggeredRef.current = true;
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [initialReels, triggerInitialAnimations]);

  // Reset trigger flag when initialReels clears (feature ends)
  useEffect(() => {
    if (!initialReels || initialReels.length === 0) {
      idleAnimationsTriggeredRef.current = false;
    }
  }, [initialReels]);

  // Match the same X/Y offset used by the base game's GameContainer
  const containerX = useMemo(() => dimensions.width / 2, [dimensions.width]);

  // Match the same Y offset used by the base game's GameContainer
  // so the feature reels sit at the same vertical position within their bone
  const containerY = useMemo(() => {
    const realDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;
    const deviceConfig = GAME_CONTAINER_OFFSET_CONFIG[realDevice];
    const offsetMultiplier =
      typeof deviceConfig === "number"
        ? deviceConfig
        : (deviceConfig?.[orientation] ?? 0.325);
    return dimensions.height / 2 - symbolHeight * offsetMultiplier;
  }, [dimensions.height, symbolHeight, device, orientation, isMobileSmall]);

  return (
    <Container
      ref={featureContainerRef}
      name="royal_collect_game_container"
      sortableChildren
      eventMode="passive"
      x={containerX}
      y={containerY}
      zIndex={10}
    >
      <Container
        ref={featureReelContainerRef}
        name="feature_reel_container"
        x={0}
        y={0}
        alpha={introPopupActive || menuActive ? 0.001 : 1}
        sortableChildren
        eventMode="passive"
      >
        <RoyalCollectReelBack
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dimensions={dimensions}
          dividerDimensions={dividerDimensions}
        />

        <RoyalCollectReelSet
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          reelSpacing={reelSpacing}
        />

        <RoyalCollectReelFront
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dimensions={dimensions}
          dividerDimensions={dividerDimensions}
        />

        <RoyalCollectOverlayReelSet
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          reelSpacing={reelSpacing}
        />

        <RoyalCollectCollectionLayer
          ref={royalCollectCollectionLayerRef}
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
        />
      </Container>

      {/* Jackpots bar — injected into RC reel_front spine's jackpots_position slot via SlotRegistry */}
      {FEATURES_CONFIG.JACKPOTS.ENABLED && <Jackpots scope="rc" />}

      {/* Respin counter — injected into RC reel_front spine's bonus_counter slot via SlotRegistry */}
      <RoyalCollectRespinCounter />

      {/* Feature logos — injected into background spine bone slots via SlotRegistry */}
      {!introPopupActive && !menuActive && (
        <>
          <LogoLandscape target="feature" renderMode="spine" />
          <LogoPortrait target="feature" renderMode="spine" />
        </>
      )}
    </Container>
  );
};

RoyalCollectGameContainer.displayName = "RoyalCollectGameContainer";

export default RoyalCollectGameContainer;
